// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import config from 'config';

import { useImperativeTimer } from 'hooks/timeout';
import useTracking from 'hooks/analytics';

import Button from 'components/common/Button';
import { FocusGroup } from 'components/focus';
import LogoPage from 'components/pages/LogoPage';
import { usePlayerAction } from 'components/providers/PlayerStateContext';

import focusManager from 'support/focus/FocusManager';
import textToSpeechManager from 'support/tts/TextToSpeechManager';
import appActions from 'common/support/applicationActions';
import { getPageValues } from 'support/analytics/analyticsHelper';
import {
  disableAreYouStillWatching,
  showAreYouStillWatching,
  hideAreYouStillWatching,
} from 'actions/areYouStillWatching';

import styles from './AreYouStillWatching.module.scss';

const noResponseTimeout = config.areYouStillWatching.noResponseTimeoutMs;

const messages = {
  step1Message: {
    id: 'areYouStillWatching.step1.message',
    description: 'step 1 message',
  },
  step1ButtonYes: {
    id: 'areYouStillWatching.step1.buttonYes',
    description: 'step 1 button 1',
  },
  step1ButtonYesDoNotShowAgain: {
    id: 'areYouStillWatching.step1.buttonYesDoNotShowAgain',
    description: 'step 1 button 2',
  },
  step2MessageLine1: {
    id: 'areYouStillWatching.step2.message.line1',
    description: 'step 2 message line 1',
  },
  step2MessageLine2: {
    id: 'areYouStillWatching.step2.message.line2',
    description: 'step 2 message line 2',
  },
};

const trackAYSWImpression = (trackEvent, { videoTitle, gameId }) => {
  const eventData = {
    moduleName: 'live video player',
    moduleLocation: 'are you still watching prompt',
    moduleAction: 'impression',
    gameTitle: videoTitle,
    gameCode: gameId,
  };

  trackEvent('are you still watching impression', eventData);
};

const trackAYSWInteraction = (
  trackEvent,
  { videoTitle, gameId, clickText },
) => {
  const eventData = {
    moduleName: 'live video player',
    moduleLocation: 'are you still watching prompt',
    moduleAction: 'click',
    gameTitle: videoTitle,
    gameCode: gameId,
    clickText,
  };

  trackEvent('are you still watching click', eventData);
};

const trackAYSWTimeout = (trackPage, { videoTitle, gameId }) => {
  const pageData = {
    ...getPageValues('inactivity'),
    pageType: 'timeout',
    gameTitle: videoTitle,
    gameCode: gameId,
  };

  trackPage(pageData);
};

const trackAYSWTimeoutClick = (trackEvent, { videoTitle, gameId }) => {
  const eventData = {
    moduleName: 'live video player',
    moduleLocation: 'are you still watching timeout',
    moduleAction: 'click',
    gameTitle: videoTitle,
    gameCode: gameId,
    clickText: 'back',
  };

  trackEvent('are you still watching click', eventData);
};

export const useAreYouStillWatchingTimer = () => {
  const currentVideoId = useSelector(state => state.currentVideo.videoId);
  const timeoutValue = useSelector(
    state =>
      state.kitchenSink?.data?.config?.modules?.linearChannelWatchingTimeouts
        ?.params[currentVideoId],
  );
  const isFeedEnabled = useSelector(
    state =>
      state.kitchenSink?.data?.config?.modules?.linearChannelWatchingTimeouts
        ?.enabled,
  );

  const stillWatchingTimeout = timeoutValue * 60 * 1000;

  const isDisabledManually = useSelector(
    state => state.areYouStillWatching?.disabled,
  );

  const isAnotherDialogOpen = useSelector(state => state.dialog.isOpen);

  const dispatch = useDispatch();
  const timerRef = useRef();
  timerRef.current = useImperativeTimer(() => {
    if (
      isFeedEnabled &&
      !!timeoutValue &&
      !isDisabledManually &&
      !isAnotherDialogOpen
    ) {
      dispatch(showAreYouStillWatching());
    }
  }, stillWatchingTimeout);
  useEffect(() => {
    timerRef.current.restart();
  }, [stillWatchingTimeout, timeoutValue]);

  useEffect(() => {
    if (isDisabledManually) {
      timerRef.current.stop();
    } else {
      timerRef.current.restart();
    }
  }, [isDisabledManually]);

  useEffect(() => {
    const resetTimer = () => {
      timerRef.current.restart();
    };
    document.addEventListener('keydown', resetTimer);
    return () => {
      document.removeEventListener('keydown', resetTimer);
    };
  }, []);
};

const AreYouStillWatching = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { pause, resetResource } = usePlayerAction();
  const { trackPage, trackEvent } = useTracking();

  const currentVideoTitle = useSelector(
    state => state.currentVideo?.video?.about?.title,
  );
  const currentVideoGameId = useSelector(
    state => state.currentVideo?.video?.properties?.gameId,
  );

  const showAYSW = useSelector(state => state.areYouStillWatching?.showDialog);

  const [step, setStep] = useState(1);

  const videoContextRef = useRef({
    videoTitle: currentVideoTitle,
    videoGameId: currentVideoGameId,
  });
  useEffect(() => {
    videoContextRef.current = {
      videoTitle: currentVideoTitle,
      videoGameId: currentVideoGameId,
    };
  });

  useEffect(() => {
    trackAYSWImpression(trackEvent, {
      videoTitle: videoContextRef.current.videoTitle,
      gameId: videoContextRef.current.videoGameId,
    });
  }, [trackEvent]);

  useEffect(() => {
    return () => {
      textToSpeechManager.stop();
    };
  }, []);

  useEffect(() => {
    let timeout;
    if (showAYSW && step === 1) {
      timeout = setTimeout(() => {
        trackAYSWTimeout(trackPage, {
          videoTitle: videoContextRef.current.videoTitle,
          gameId: videoContextRef.current.videoGameId,
        });
        pause();
        setStep(2);
      }, noResponseTimeout);

      return () => {
        clearTimeout(timeout);
      };
    }

    if (showAYSW && step === 2) {
      const resetTimer = () => {
        trackAYSWTimeoutClick(trackEvent, {
          videoTitle: videoContextRef.current.videoTitle,
          gameId: videoContextRef.current.videoGameId,
        });
        dispatch(hideAreYouStillWatching());
        resetResource();
        setStep(1);
      };

      document.addEventListener('keydown', resetTimer);
      return () => {
        document.removeEventListener('keydown', resetTimer);
      };
    }

    return undefined;
  }, [step, pause, resetResource, trackPage, trackEvent, showAYSW, dispatch]);

  useEffect(() => {
    if (!showAYSW) return;
    if (step === 1) {
      textToSpeechManager.clear();
      textToSpeechManager.add(intl.formatMessage(messages.step1Message));
      focusManager.setCurrentFocus('step1-yes-button');
    }
    if (step === 2) {
      textToSpeechManager.clear();
      textToSpeechManager.add(intl.formatMessage(messages.step2MessageLine1));
      textToSpeechManager.add(intl.formatMessage(messages.step2MessageLine2));
      textToSpeechManager.speak();
    }
  }, [step, intl, showAYSW]);

  const trackStep1ButtonClick = buttonText => {
    trackAYSWInteraction(trackEvent, {
      videoTitle: videoContextRef.current.videoTitle,
      gameId: videoContextRef.current.videoGameId,
      clickText: buttonText,
    });
  };

  const onActionOverride = action => {
    if (showAYSW && action === appActions.back && step === 1) {
      dispatch(hideAreYouStillWatching());
      trackStep1ButtonClick('back');
    }
    return false;
  };

  return (
    <React.Fragment>
      {showAYSW && (
        <div
          className={classNames('w-100 h-100', styles.mainContainer)}
          data-testid="are-you-still-watching-page"
        >
          {step === 1 && (
            <FocusGroup
              focusId="stillwatching-button-column"
              className="d-flex flex-column align-items-center justify-content-center h-100"
              onActionOverride={onActionOverride}
            >
              <div className={styles.step1}>
                <span className={styles.step1Message}>
                  <FormattedMessage {...messages.step1Message} />
                </span>
                <Button
                  className={classNames('mt-4', styles.step1Button)}
                  onClick={() => {
                    dispatch(hideAreYouStillWatching());
                    trackStep1ButtonClick(
                      intl.formatMessage(messages.step1ButtonYes),
                    );
                  }}
                  size="lg"
                  ttsText={intl.formatMessage(messages.step1ButtonYes)}
                  focusId="step1-yes-button"
                  data-testid="step1-yes-button"
                >
                  {intl.formatMessage(messages.step1ButtonYes)}
                </Button>
                <Button
                  className={classNames('mt-4', styles.step1Button)}
                  onClick={() => {
                    dispatch(disableAreYouStillWatching());
                    dispatch(hideAreYouStillWatching());
                    trackStep1ButtonClick(
                      intl.formatMessage(messages.step1ButtonYesDoNotShowAgain),
                    );
                  }}
                  size="lg"
                  ttsText={intl.formatMessage(
                    messages.step1ButtonYesDoNotShowAgain,
                  )}
                  focusId="step1-do-not-show-again-button"
                  data-testid="step1-do-not-show-again-button"
                >
                  {intl.formatMessage(messages.step1ButtonYesDoNotShowAgain)}
                </Button>
              </div>
            </FocusGroup>
          )}
          {step === 2 && (
            <LogoPage className="position-relative">
              <div
                className={classNames(
                  'd-flex flex-column align-items-center justify-content-center',
                  'h-100',
                  'font-weight-bold',
                  styles.step2Message,
                )}
                data-testid="step-2"
              >
                <span>
                  <FormattedMessage {...messages.step2MessageLine1} />
                </span>
                <span>
                  <FormattedMessage {...messages.step2MessageLine2} />
                </span>
              </div>
            </LogoPage>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default React.memo(AreYouStillWatching);
